import { apiFetch, type PaginatedResponse } from './api';
import type { PteroEnvId } from './pterodactyl-config.service';

export interface PromoteRequest {
  targetEnv: PteroEnvId;
  sourceEnv?: PteroEnvId;
  dryRun?: boolean;
  /** Nom de la procédure à exécuter (défaut: "Défaut"). */
  procedureName?: string;
  /**
   * Promotion sans le modèle des lobbies de Nexus (eggs) : seulement quand la tournée ne change pas
   * la liste d'objets. Sinon une promotion dont l'egg ne peut pas être mis à jour est refusée.
   */
  skipEggs?: boolean;
}

export interface PromoteStepResult {
  stepIndex: number;
  type: string;
  label: string;
  ok: boolean;
  error?: string;
  durationMs: number;
  matchedServers?: number;
  matchedServerNames?: string[];
  warnings?: string[];
  /** Phase de la promotion d'un bloc (preflight, prepare, proxy-stop, eggs, body, check, proxy-start, reopen, rollback). */
  phase?: string;
}

/** Fin d'une promotion : absente tant qu'elle tourne. */
export type PromotionOutcome =
  | 'ok' | 'stored' | 'refused' | 'cancelled' | 'halted' | 'proxy-not-restarted' | 'simulated' | 'interrupted';

/** État du proxy pendant et après la promotion. */
export type ProxyState = 'untouched' | 'stopped' | 'restarted';

export interface PromotionJobSnapshot {
  jobId: string;
  projectId: string;
  targetEnv: PteroEnvId;
  sourceEnv: PteroEnvId;
  done: boolean;
  currentStepIndex: number;
  totalSteps: number;
  results: PromoteStepResult[];
  error?: string;
  startedAt: string;
  endedAt?: string;
  /** Qui a lancé la promotion (relevé au démarrage, côté API). */
  actorId?: string;
  actorName?: string;
  dryRun?: boolean;
  procedureName?: string;
  /** Lancée « sans le modèle des lobbies ». */
  skipEggs?: boolean;
  /** Jouée d'un bloc : proxy arrêté d'abord, redémarré en dernier. */
  bloc?: boolean;
  outcome?: PromotionOutcome;
  proxyState?: ProxyState;
  proxyStoppedAt?: string;
  proxyStartedAt?: string;
  /** Une phrase de bilan. */
  summary?: string;
  /** Quoi faire maintenant (refus, échec). */
  nextSteps?: string[];
  /** Étapes non jouées après un arrêt. */
  notRun?: string[];
  /** Eggs restés derrière leurs serveurs : geste « Aligner l'egg sur les serveurs ». */
  eggsBehind?: string[];
}

/** Une étape de l'ordre joué (aperçu de la fenêtre de promotion). */
export interface PromotionPlanItem {
  phase: string;
  phaseLabel: string;
  label: string;
  type: string;
  /** Numéro dans la procédure enregistrée, 0 pour une étape du CICD. */
  storedIndex: number;
  builtin: boolean;
  /** Proxys retirés des serveurs visés. */
  gameOnly: boolean;
  detail?: string;
}

export interface PromotionPlan {
  /** Promotion d'un bloc (la procédure met des images à jour). */
  bloc: boolean;
  items: PromotionPlanItem[];
  joinedStop: string[];
  joinedStart: string[];
  skipped: string[];
  warnings: string[];
  errors: string[];
}

/** Résultat d'un geste du compte rendu (« Démarrer le proxy », « Aligner l'egg »). */
export interface PromotionActionResult {
  ok: boolean;
  summary: string;
  lines: string[];
}

export const promotionService = {
  /** Lance un job async, renvoie immédiatement avec le jobId. */
  start: (projectId: string, req: PromoteRequest) =>
    apiFetch<PromotionJobSnapshot>(`/api/projects/${projectId}/promote`, {
      method: 'POST', json: req,
    }),
  /** Polling : récupère l'état actuel du job. */
  status: (projectId: string, jobId: string) =>
    apiFetch<PromotionJobSnapshot>(`/api/projects/${projectId}/promote/jobs/${jobId}`),
  /** Suivi PARTAGÉ : liste les jobs (actifs + récents) du projet — visible par tous (cicd.view). */
  listJobs: (projectId: string) =>
    apiFetch<PromotionJobSnapshot[]>(`/api/projects/${projectId}/promote/jobs`),
  /** Historique PERSISTANT (au-delà de la fenêtre 30 min des jobs en mémoire), paginé. */
  history: (projectId: string, page = 0, pageSize = 25) =>
    apiFetch<PaginatedResponse<PromotionJobSnapshot>>(
      `/api/projects/${projectId}/promote/history?page=${page}&pageSize=${pageSize}`,
    ),
  /** Ordre que jouera la procédure (calculé sans appeler Pterodactyl). */
  plan: (projectId: string, targetEnv: PteroEnvId, procedureName: string) =>
    apiFetch<PromotionPlan>(
      `/api/projects/${projectId}/promote/plan?targetEnv=${targetEnv}&procedureName=${encodeURIComponent(procedureName)}`,
    ),
  /** Démarre le ou les proxys du projet (après une promotion arrêtée). */
  startProxies: (projectId: string, targetEnv: PteroEnvId) =>
    apiFetch<PromotionActionResult>(`/api/projects/${projectId}/promote/proxies/start`, {
      method: 'POST', json: { targetEnv },
    }),
  /** Met les eggs sur la version de leurs serveurs (egg resté derrière). */
  alignEggs: (projectId: string, targetEnv: PteroEnvId) =>
    apiFetch<PromotionActionResult>(`/api/projects/${projectId}/promote/eggs/align`, {
      method: 'POST', json: { targetEnv },
    }),
};
